// Servicio de autenticación (Fase B, VLT-035/018/019/249).
// Patrón STRATEGY: la UI solo conoce esta interfaz. Con backend real usa
// Supabase Auth; sin variables de entorno cae al modo demo (Fase A) para que
// previews y CI nunca se rompan. Errores → claves i18n, nunca texto técnico
// (Constitución §15).
import { supabase, hasBackend } from '@/lib/supabase';
import { CURRENT_LEGAL_VERSIONS } from './legal';
import { authEventsService } from '@/services/authEvents';

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
}

export interface SignUpInput {
  email: string;
  password: string;
  fullName: string;
  /** Casilla de términos y privacidad marcada (sin marcar de antemano). */
  acceptLegal?: boolean;
  /** Ausente en el registro por invitación de equipo (VLT-405): el perfil
   *  nace en la organización invitante vía el trigger 0023. */
  orgName?: string;
  /** Empresa preparadora con equipo, o persona física independiente. */
  accountType?: 'firm' | 'independent';
}

export type AuthResult =
  | { ok: true; user: SessionUser | null; needsEmailConfirm?: boolean; needsMfa?: boolean }
  | { ok: false; errorKey: string; minutes?: number };

const DEMO_USER: SessionUser = {
  id: 'demo-user',
  email: 'gabriela@cassimmit.tax',
  fullName: 'Gabriela C.',
};

function toSessionUser(u: { id: string; email?: string; user_metadata?: Record<string, unknown> } | null): SessionUser | null {
  if (!u) return null;
  return {
    id: u.id,
    email: u.email ?? '',
    fullName: (u.user_metadata?.full_name as string | undefined) ?? u.email ?? '',
  };
}

/** VLT-069 (0069): portero del login. Pide un ticket al Worker ANTES de cada
 *  intento con contraseña; la base cuenta los fallos y bloquea de forma
 *  progresiva, y el hook de token de Supabase exige el ticket. Si el portero
 *  no responde (sin configurar, caído) se sigue: decide el hook. */
export type GateResult = { ok: true } | { ok: false; retryAfter: number };

export async function loginGate(email: string): Promise<GateResult> {
  try {
    const res = await fetch('/api/auth/login-gate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (res.status === 429) {
      const body = (await res.json().catch(() => null)) as { retryAfter?: number } | null;
      const retryAfter = Number(body?.retryAfter ?? res.headers.get('Retry-After'));
      return { ok: false, retryAfter: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 300 };
    }
    return { ok: true };
  } catch {
    return { ok: true };
  }
}

/** Minutos de espera redondeados hacia arriba (mínimo 1) para el mensaje. */
export const lockMinutes = (retryAfter: number) => Math.max(1, Math.ceil(retryAfter / 60));

export const authService = {
  /** True cuando la sesión corre contra Supabase real. */
  isReal: hasBackend,

  async signInWithPassword(email: string, password: string, captchaToken?: string): Promise<AuthResult> {
    // El portero va primero también en demo: sin Worker responde 404 y se
    // sigue (así el aviso de bloqueo se puede probar de punta a punta).
    const gate = await loginGate(email);
    if (!gate.ok) return { ok: false, errorKey: 'auth.errors.locked', minutes: lockMinutes(gate.retryAfter) };
    if (!hasBackend) return { ok: true, user: DEMO_USER };
    try {
    // VLT-018: el token del captcha lo verifica Supabase Auth con su secret
    // (captcha del panel) — aquí solo viaja si el widget está activo.
    const { data, error } = await supabase.auth.signInWithPassword({
      email, password,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });
    if (error) {
      return {
        ok: false,
        errorKey: error.message.includes('Invalid login') ? 'auth.errors.invalid'
          // El hook (0069) negó el token: login sin ticket o cuenta bloqueada.
          : error.message.includes('login_guard') || error.status === 429 ? 'auth.errors.lockedLater'
          : 'auth.errors.generic',
      };
    }
    // MFA (VLT-307): si la cuenta tiene TOTP activo, la sesión queda en aal1
    // hasta superar el desafío — la pantalla de login pide el código.
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const needsMfa = aal?.nextLevel === 'aal2' && aal.nextLevel !== aal.currentLevel;
    void authEventsService.record('login');
    return { ok: true, user: toSessionUser(data.user), needsMfa };
    } catch {
      return { ok: false, errorKey: 'auth.errors.generic' };
    }
  },

  async signUp(input: SignUpInput, captchaToken?: string): Promise<AuthResult> {
    if (!hasBackend) return { ok: true, user: { ...DEMO_USER, email: input.email, fullName: input.fullName } };
    try {
    const { data, error } = await supabase.auth.signUp({
      email: input.email,
      password: input.password,
      // org_name/account_type los consume el trigger de aprovisionamiento
      // (0004/0010). Sin org_name (invitación de equipo, 0023) el trigger
      // busca la invitación pendiente del correo en lugar de crear firma.
      // legal_signup_*: la casilla de términos marcada al registrarse; el
      // primer ingreso la registra en la base con IP y dispositivo (0079).
      options: {
        data: {
          full_name: input.fullName,
          ...(input.orgName ? { org_name: input.orgName, account_type: input.accountType ?? 'firm' } : {}),
          ...(input.acceptLegal
            ? { legal_signup_at: new Date().toISOString(), legal_signup_versions: CURRENT_LEGAL_VERSIONS }
            : {}),
        },
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    if (error) {
      return {
        ok: false,
        errorKey: error.message.includes('already registered') ? 'auth.errors.exists' : 'auth.errors.generic',
      };
    }
    // Sin sesión inmediata = Supabase exige confirmación por correo.
    return { ok: true, user: toSessionUser(data.user), needsEmailConfirm: !data.session };
    } catch {
      return { ok: false, errorKey: 'auth.errors.generic' };
    }
  },

  // 'azure' = Microsoft en Supabase Auth (activar cada proveedor = llaves
  // OAuth en el panel — pasada de conexiones; sin ellas, error honesto).
  async signInWithOAuth(provider: 'google' | 'apple' | 'azure'): Promise<AuthResult> {
    if (!hasBackend) return { ok: true, user: DEMO_USER };
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: window.location.origin },
      });
      // Si no hay error el navegador redirige al proveedor; no hay usuario aún.
      return error ? { ok: false, errorKey: 'auth.errors.oauth' } : { ok: true, user: null };
    } catch {
      return { ok: false, errorKey: 'auth.errors.oauth' };
    }
  },

  async signOut(opts: { silent?: boolean } = {}): Promise<void> {
    // El evento se registra ANTES de cortar la sesión (después ya no hay JWT).
    // silent: el cierre ya quedó registrado con su motivo (inactividad o
    // sesión vencida, VLT-064) — no se duplica como «Cierre de sesión».
    if (!opts.silent) await authEventsService.record('logout');
    if (hasBackend) await supabase.auth.signOut();
  },

  /** VLT-065: enlace de restablecimiento por correo. Respuesta NEUTRA siempre
   *  (existe o no la cuenta) — sin enumeración de usuarios. */
  async requestPasswordReset(email: string): Promise<{ ok: boolean }> {
    if (!hasBackend) return { ok: true };
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      // El error NO se muestra al usuario (revelaría si la cuenta existe);
      // queda en consola para diagnóstico.
      if (error) console.warn('[VaulTex] auth.reset:', error.message);
      return { ok: true };
    } catch {
      return { ok: true };
    }
  },

  /** Fija la nueva contraseña (la sesión de recuperación viene del enlace del
   *  correo — detectSessionInUrl la abre sola) e invalida las DEMÁS sesiones. */
  async updatePassword(newPassword: string): Promise<AuthResult> {
    if (!hasBackend) return { ok: true, user: DEMO_USER };
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        return {
          ok: false,
          errorKey: error.message.includes('should be different') ? 'auth.errors.samePassword' : 'auth.errors.generic',
        };
      }
      void authEventsService.record('password_updated');
      // VLT-065: tras el cambio, las sesiones abiertas en otros dispositivos caen.
      await supabase.auth.signOut({ scope: 'others' });
      return { ok: true, user: toSessionUser(data.user) };
    } catch {
      return { ok: false, errorKey: 'auth.errors.generic' };
    }
  },

  /** ¿Hay sesión de recuperación activa? (el usuario llegó del enlace del correo) */
  async hasRecoverySession(): Promise<boolean> {
    if (!hasBackend) return true; // demo: la pantalla siempre es navegable
    const { data } = await supabase.auth.getSession();
    return Boolean(data.session);
  },

  /** Reenvía el correo de verificación de la cuenta recién creada. */
  async resendVerification(email: string): Promise<{ ok: boolean }> {
    if (!hasBackend) return { ok: true };
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) console.warn('[VaulTex] auth.resend:', error.message);
      return { ok: true }; // neutro: sin enumeración
    } catch {
      return { ok: true };
    }
  },

  /** Cierra la sesión en TODOS los demás dispositivos (la actual sigue viva). */
  async signOutOthers(): Promise<boolean> {
    if (!hasBackend) {
      void authEventsService.record('session_revoke_others');
      return true;
    }
    try {
      const { error } = await supabase.auth.signOut({ scope: 'others' });
      if (error) {
        console.warn('[VaulTex] auth.signOutOthers:', error.message);
        return false;
      }
      void authEventsService.record('session_revoke_others');
      return true;
    } catch {
      return false;
    }
  },

  async getUser(): Promise<SessionUser | null> {
    if (!hasBackend) return DEMO_USER;
    const { data } = await supabase.auth.getSession();
    return toSessionUser(data.session?.user ?? null);
  },

  /** Rol y organización del usuario autenticado (perfil en BD). */
  async getProfileMeta(userId: string): Promise<{ role: string; orgName: string } | null> {
    if (!hasBackend) return { role: 'admin', orgName: 'Cassimmit Tax Group' };
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role, organizations(name)')
        .eq('id', userId)
        .maybeSingle();
      if (error || !data) {
        if (error) console.warn('[VaulTex] auth.profile:', error.message);
        return null;
      }
      const row = data as unknown as { role: string; organizations: { name: string } | null };
      return { role: row.role, orgName: row.organizations?.name ?? '' };
    } catch {
      return null;
    }
  },

  /** Suscripción a cambios de sesión; devuelve el unsubscribe. */
  onAuthChange(cb: (user: SessionUser | null) => void): () => void {
    if (!hasBackend) return () => undefined;
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      cb(toSessionUser(session?.user ?? null));
    });
    return () => data.subscription.unsubscribe();
  },
};

// ── OTP de login por correo (12-sep) ────────────────────────────────────────
// Segundo factor para cuentas SIN MFA por aplicación: tras la contraseña, el
// Worker manda un código de 6 dígitos al correo y la pantalla lo pide.
// «Recordar este equipo» guarda el token del Worker (30 días) y /send
// responde skip sin generar código. Si el Worker no tiene las llaves de
// correo (503) el paso se OMITE honesto — nadie queda fuera por
// infraestructura a medio configurar (patrón Turnstile).
const TRUSTED_DEVICE_KEY = 'vaultex.trustedDevice';
/** Código fijo del modo demo — visible en la pantalla para poder probar. */
export const DEMO_OTP_CODE = '482913';

export type EmailOtpBegin = 'sent' | 'skip' | 'unavailable';

function readTrustedDevice(): string | null {
  try { return localStorage.getItem(TRUSTED_DEVICE_KEY); } catch { return null; }
}
function writeTrustedDevice(token: string): void {
  try { localStorage.setItem(TRUSTED_DEVICE_KEY, token); } catch { /* privado/bloqueado: sin recordar */ }
}

export const emailOtpService = {
  /** Pide el envío del código. 'skip' = equipo recordado; 'unavailable' =
   *  correo no configurado o red caída → el login sigue sin este paso. */
  async begin(lang: 'es' | 'en'): Promise<EmailOtpBegin> {
    if (!hasBackend) return readTrustedDevice() === 'demo-trusted' ? 'skip' : 'sent';
    try {
      const { data } = await supabase.auth.getSession();
      const jwt = data.session?.access_token;
      if (!jwt) return 'unavailable';
      const res = await fetch('/api/auth/email-otp/send', {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang, ...(readTrustedDevice() ? { deviceToken: readTrustedDevice() } : {}) }),
      });
      if (!res.ok) return 'unavailable';
      const out = (await res.json()) as { sent?: boolean; skip?: boolean };
      return out.skip ? 'skip' : 'sent';
    } catch {
      return 'unavailable';
    }
  },

  /** ¿La sesión actual ya cumple el segundo factor según el servidor (0077)?
   *  null = no se pudo saber (red o Worker sin configurar): no se decide nada. */
  async status(): Promise<{ required: boolean; ok: boolean } | null> {
    if (!hasBackend) return null;
    try {
      const { data } = await supabase.auth.getSession();
      const jwt = data.session?.access_token;
      if (!jwt) return null;
      const res = await fetch('/api/auth/email-otp/status', {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) return null;
      const out = (await res.json()) as { required?: boolean; ok?: boolean };
      return { required: out.required === true, ok: out.ok === true };
    } catch {
      return null;
    }
  },

  /** Verifica el código en el servidor; con rememberDevice guarda el token
   *  para que los próximos logins de este equipo salten el paso. */
  async verify(code: string, rememberDevice: boolean): Promise<boolean> {
    if (!hasBackend) {
      if (code.trim() !== DEMO_OTP_CODE) return false;
      if (rememberDevice) writeTrustedDevice('demo-trusted');
      return true;
    }
    try {
      const { data } = await supabase.auth.getSession();
      const jwt = data.session?.access_token;
      if (!jwt) return false;
      const res = await fetch('/api/auth/email-otp/verify', {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), rememberDevice }),
      });
      if (!res.ok) return false;
      const out = (await res.json()) as { ok?: boolean; deviceToken?: string | null };
      if (out.ok && rememberDevice && out.deviceToken) writeTrustedDevice(out.deviceToken);
      return Boolean(out.ok);
    } catch {
      return false;
    }
  },
};
